import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { toDateOnly } from '@voll-intelligence/dates';
import { useGrowNearEnd, useGrowNearStart, useOpenAtMonth } from './date_sheet_body.js';

/*
 * The months of the `TravelCalendar`: which month is in view and how long the sheet's column is.
 * Out of `travel_calendar.tsx` for its line cap.
 */

/** A coluna da folha nasce com este tanto de meses e cresce em passos iguais
 *  quando o rolo se aproxima do fim (scroll infinito, #3446): a janela não tem
 *  teto — o popover também pagina sem fim. */
const INITIAL_MONTHS = 4;
const GROW_STEP = 3;

/** `YYYY-MM` deslocado `n` meses. */
function addMonths(month: string, n: number): string {
  const date = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + n, 1);
  return toDateOnly(date).slice(0, 7);
}

/** `count` meses `YYYY-MM` consecutivos a partir de `from`, inclusive. */
export function monthsFrom(from: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addMonths(from, i));
}

export function earlierMonth(a: string, b: string): string {
  return a < b ? a : b;
}

function laterMonth(a: string, b: string | undefined): string {
  return b !== undefined && b > a ? b : a;
}

/** How many months from `from` to `to`, inclusive (1 when they are the same). */
function monthSpan(from: string, to: string): number {
  const index = (month: string) => Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7));
  return index(to) - index(from) + 1;
}

export function monthStart(month: string): Date {
  return new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1);
}

/** O primeiro mês em cena: o pedido (o `month` do popover, a âncora da folha)
 *  enquanto o piso couber no que ele mostra. Re-ancorar no piso só quando a
 *  vista inteira acaba antes dele — o piso no 2º mês do par (a ida escolhida à
 *  direita) não empurra a vista (#4280) — ou, na folha, quando ele fica acima
 *  da coluna, que não sobe. Meses ISO comparam cronologicamente como texto. */
function anchoredMonth({
  requested,
  floorMonth,
  count,
  stacked,
}: {
  readonly requested: string;
  readonly floorMonth: string | undefined;
  readonly count: number;
  readonly stacked: boolean;
}): string {
  if (floorMonth === undefined) return requested;
  const endsBeforeFloor = addMonths(requested, count - 1) < floorMonth;
  const floorAboveColumn = stacked && floorMonth < requested;
  return endsBeforeFloor || floorAboveColumn ? floorMonth : requested;
}

/** The months the `past` and `free` sheets show around the value's month (`pivot`): `up` months
 *  above it, stopping at the floor when there is one, down to the ceiling (`past`) or `down`
 *  months below it (`free`). */
function pivotColumn({
  pivot,
  up,
  down,
  floorMonth,
  ceilingMonth,
}: {
  readonly pivot: string;
  readonly up: number;
  readonly down: number;
  readonly floorMonth: string | undefined;
  readonly ceilingMonth: string | undefined;
}): { month: string; count: number } {
  const month = laterMonth(addMonths(pivot, -up), floorMonth);
  return { month, count: monthSpan(month, ceilingMonth ?? addMonths(pivot, down)) };
}

/** How many months the pivot column is born with above the value's month: the `past` column fills
 *  `INITIAL_MONTHS` up to the ceiling, the `free` one opens one month above. */
function initialUp(pivot: string, ceilingMonth: string | undefined): number {
  return ceilingMonth === undefined
    ? 1
    : Math.max(0, INITIAL_MONTHS - monthSpan(pivot, ceilingMonth));
}

/** The sheet column's growth: down from the floor (the searches), up toward the past, or both
 *  (`free`). A pivot column is born scrolled to the value's month. */
function useColumnGrowth({
  down,
  up,
  grow,
}: {
  readonly down: (() => void) | undefined;
  readonly up:
    | {
        readonly month: string;
        readonly count: number;
        readonly floorMonth: string | undefined;
        readonly pivot: string;
        readonly grow: () => void;
      }
    | undefined;
  readonly grow: () => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  useGrowNearEnd(scrollerRef, { enabled: down !== undefined, onNearEnd: down ?? grow });
  useGrowNearStart(scrollerRef, {
    enabled: up !== undefined && (up.floorMonth === undefined || up.month > up.floorMonth),
    size: up?.count ?? 0,
    onNearStart: up?.grow ?? grow,
  });
  useOpenAtMonth(scrollerRef, up ? monthSpan(up.month, up.pivot) - 1 : undefined);
  return scrollerRef;
}

/** The anchored first month of the popover and of the column that grows down.
 *  A âncora guardada: o piso que sobe para a ida e desce quando o par fecha
 *  só desabilita dias, não move a vista nem recria a coluna sob o rolo
 *  (#4280). Uma re-âncora no piso fica até quem pede trocar o mês. */
function useAnchoredMonth({
  requested,
  floorMonth,
  initialMonth,
  count,
  stacked,
}: {
  readonly requested: string | undefined;
  readonly floorMonth: string | undefined;
  readonly initialMonth: string;
  readonly count: number;
  readonly stacked: boolean;
}): string {
  const [anchor, setAnchor] = useState({
    requested,
    month: requested ?? floorMonth ?? initialMonth,
  });
  const anchored = anchoredMonth({
    requested: anchor.requested === requested ? anchor.month : (requested ?? anchor.month),
    floorMonth,
    count,
    stacked,
  });
  if (anchor.requested !== requested || anchor.month !== anchored) {
    setAnchor({ requested, month: anchored });
  }
  return anchored;
}

/** Which way the sheet's column grows: `down` from the floor (the searches), `up` to a ceiling
 *  (the `past`), `both` around the value (`free`). The popover pages, never grows. */
export type ColumnGrowth = 'down' | 'up' | 'both';

/** O mês em cena e a coluna da folha: o `month` controlado (ou o próprio), o
 *  tanto de meses empilhados, o rolo medido e o anúncio dos meses em cena. */
export function useCalendarMonths({
  stacked,
  growth,
  floorMonth,
  ceilingMonth,
  initialMonth,
  monthProp,
  months,
  onMonthChange,
  onMonthsChange,
}: {
  readonly stacked: boolean;
  readonly growth: ColumnGrowth;
  readonly floorMonth: string | undefined;
  /** `up` only: the column's last month. */
  readonly ceilingMonth: string | undefined;
  readonly initialMonth: string;
  readonly monthProp: string | undefined;
  readonly months: 1 | 2;
  readonly onMonthChange: ((month: string) => void) | undefined;
  readonly onMonthsChange: ((months: readonly string[]) => void) | undefined;
}) {
  const [ownMonth, setOwnMonth] = useState(initialMonth);
  // O popover pede o mês (`month` de quem chama, ou o próprio); a folha nasce
  // sempre no piso — a coluna cresce a partir dele, como a do voo.
  const requested = stacked ? undefined : (monthProp ?? ownMonth);
  const [shown, setShown] = useState(INITIAL_MONTHS);
  // The pivot column (`up`, `both`) is held around the month it was born on.
  const [pivot] = useState(initialMonth);
  const [up, setUp] = useState(() => initialUp(initialMonth, ceilingMonth));
  const pivoted = stacked && growth !== 'down';
  const anchored = useAnchoredMonth({
    requested,
    floorMonth,
    initialMonth,
    count: stacked ? shown : months,
    stacked,
  });
  const { month, count } = pivoted
    ? pivotColumn({ pivot, up, down: shown - 2, floorMonth, ceilingMonth })
    : { month: anchored, count: stacked ? shown : months };
  const setMonth = (next: Date) => {
    const nextMonth = toDateOnly(next).slice(0, 7);
    setOwnMonth(nextMonth);
    onMonthChange?.(nextMonth);
  };
  const growDown = () => {
    setShown((n) => n + GROW_STEP);
  };
  const scrollerRef = useColumnGrowth({
    down: stacked && growth !== 'up' ? growDown : undefined,
    up: pivoted
      ? {
          month,
          count,
          floorMonth,
          pivot,
          grow: () => {
            setUp((n) => n + GROW_STEP);
          },
        }
      : undefined,
    grow: growDown,
  });
  // Os meses em cena, anunciados a quem pediu só quando a lista muda — lido
  // como evento para o efeito não depender da identidade do callback, que muda
  // a cada render de quem chama.
  const shownKey = monthsFrom(month, count).join(',');
  const announceMonths = useEffectEvent((months: string[]) => {
    onMonthsChange?.(months);
  });
  useEffect(() => {
    announceMonths(shownKey.split(','));
  }, [shownKey]);
  return { month, setMonth, shown: count, scrollerRef };
}
/** Os rótulos do `Calendar`: setas, dia da semana e o nome do dia
 *  (`formatCalendarDay`) com a ponta que ele é ("15 de setembro — IDA"). */
